import { useMemo } from "react";
import { connect } from "react-redux";
import { groupBy } from "lodash";
import { Link } from "react-router-dom";

import UserConnectedOrgsModal from "@/integrations/UserConnectedOrgsModal.js";
import ArchivedCompany from "../../company-pages/ArchivedCompany";
import UpgradeRequired from "../../company-pages/UpgradeRequired";

// * Time & Attendance
import DeputySettings from "@/integrations/deputy/DeputySettings";
import TimeMotoSettings from "@/integrations/timemoto/TimeMotoSettings";
import WorkforceSettings from "@/integrations/workforce/WorkforceSettings";
import ZKTecoSettings from "@/integrations/zkteco/ZKTecoSettings";

// * HRIS
import BambooSettings from "@/integrations/bamboo-hr/BambooSettings";

// * Accounting
import FreshbooksSettings from "@/integrations/freshbooks/FreshbooksSettings";
import QuickbooksSettings from "@/integrations/quickbooks/QuickbooksSettings";
import SageSettings from "@/integrations/sage/SageSettings";
import ViftoSettings from "@/integrations/vifto/ViftoSettings";
import XeroSettings from "@/integrations/xero/XeroSettings";
import AccountingIntegrationSettings from "@/integrations/accountingIntegrations/AccountingIntegrationSettings";

// * Payments
import FyorinSettings from "@/integrations/fyorin/FyorinSettings";
import PaymentIntegrationSettings from "@/integrations/paymentIntegrations/PaymentIntegrationSettings";

// * Pensions
import PeoplesPensionSettings from "@/integrations/peoplePension/PeoplesPensionSettings";
import SmartPensionSettings from "@/integrations/smartPension/SmartPensionSettings";
import PensionIntegrationSettings from "@/integrations/pensionIntegrations/PensionIntegrationSettings";

const sectionComponents = {
	Accounting: AccountingIntegrationSettings,
	Pension: PensionIntegrationSettings,
	Payment: PaymentIntegrationSettings,
};

const integrations = [
	{ section: "Time & Attendance", component: WorkforceSettings },
	{ section: "Time & Attendance", component: DeputySettings },
	{ section: "Time & Attendance", component: ZKTecoSettings },
	{ section: "Time & Attendance", component: TimeMotoSettings },
	{ section: "HRIS", component: BambooSettings },
	{ section: "Accounting", component: XeroSettings },
	{ section: "Accounting", component: SageSettings },
	{ section: "Accounting", component: QuickbooksSettings },
	{ section: "Accounting", component: FreshbooksSettings },
	{ section: "Accounting", component: ViftoSettings, jurisdiction: ["MT"] },
	{ section: "Pension", component: PeoplesPensionSettings, jurisdiction: ["UK"] },
	{ section: "Pension", component: SmartPensionSettings, jurisdiction: ["UK"] },
	{ section: "Payment", component: FyorinSettings },
];

const Integrations = ({
	includedInPlan,
	jurisdiction,
	isDemo,
	archivedStatus,
	section,
	integrationDetails,
}) => {
	const integrationList = useMemo(() => {
		const integrationList = integrationDetails.map((integration) => {
			return { ...integration, component: sectionComponents[integration.section] };
		});

		return Object.entries(
			groupBy(
				[...integrations, ...integrationList].filter((integration) => {
					const jurisdictionMatches =
						!integration.jurisdiction || integration.jurisdiction?.includes(jurisdiction);

					const sectionMatches = !section || integration.section == section;
					return jurisdictionMatches && sectionMatches;
				}),
				"section",
			),
		);
	}, [jurisdiction, JSON.stringify(integrationDetails)]);

	if (archivedStatus == "Archived") return <ArchivedCompany />;
	if (!includedInPlan && !isDemo) return <UpgradeRequired />;

	return (
		<div>
			<UserConnectedOrgsModal />
			{integrationList.map(([label, integrations], i) => {
				return (
					<IntegrationSection
						key={i}
						label={label}
						section={section}
						integrations={integrations}
					/>
				);
			})}
			<div className="clearfix" />
		</div>
	);
};

const IntegrationSection = ({ section, label, integrations }) => {
	return (
		<div className="col-xs-12">
			<Link
				id={`${label}`}
				style={{
					color: "inherit",
					scrollMarginTop: "8rem",
					cursor: "text",
					display: "block",
					fontSize: "2.4rem",
					fontWeight: 600,
					lineHeight: 1.2,
					marginBlockStart: "1em",
					marginBlockEnd: "1em",
					marginInlineStart: "0px",
					marginInlineEnd: "0px",
					boxSizing: "border-box",
				}}
			>
				{label}
				{section ? " Integrations" : ""}
			</Link>
			{integrations.map((integration, i) => {
				if (!integration.component) return null;
				const Component = integration.component;
				return (
					<Component
						key={i}
						integration={integration.name}
					/>
				);
			})}
			<div className="clearfix" />
		</div>
	);
};

const mapStateToProps = (state) => {
	const integrationDetails = Object.values({ ...state.integrationDetails })?.flatMap((section) => {
		return Object.values(section);
	});

	return {
		includedInPlan: state.permissions?.plan?.integrations,
		integrationDetails,
		jurisdiction: state.company?.settings?.jurisdiction,
		isDemo: state.company?.settings?.isDemo,
		archivedStatus: state?.company?.settings?.status,
	};
};
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Integrations);
